/**
 * @file functionErrorMessage.js
 * @description The one place that turns a supabase.functions.invoke() error into a readable reason.
 *   supabase-js wraps every non-2xx reply in a FunctionsHttpError whose generic message is useless
 *   ("Edge Function returned a non-2xx status code"); the function's own JSON body (`error`, `errors`
 *   or `reason`) lives on error.context, which is the raw Response.
 * @module lib/functionErrorMessage
 */

/** First readable reason in a function reply body, or "". */
export function reasonFromBody(body) {
  if (!body || typeof body !== "object") return "";
  if (Array.isArray(body.errors) && body.errors.length) return body.errors.join("; ");
  if (typeof body.error === "string" && body.error) return body.error;
  if (typeof body.reason === "string" && body.reason) return body.reason;
  return "";
}

/**
 * The JSON body of a function error's reply (error.context is the raw Response), or null when there is none or it is
 * not JSON. Never throws. It reads a clone when the Response has one, so several readers of the same error (the
 * payment screens check a spend cap, then a refusal code, then log the reason) each get the body.
 * @param {Error|null|undefined} error the `error` from supabase.functions.invoke
 * @returns {Promise<object|null>}
 */
export async function functionErrorBody(error) {
  try {
    const res = error?.context;
    const body = await (typeof res?.clone === "function" ? res.clone() : res)?.json?.();
    return body && typeof body === "object" ? body : null;
  } catch {
    return null; // body was not JSON
  }
}

/**
 * Reads the real reason from a function error: the reply body first, then the error's own message.
 * Never throws (a body that is not JSON, or was already read, falls back to the message).
 * @param {Error|null|undefined} error the `error` from supabase.functions.invoke
 * @param {string} [fallback] used when there is no reason at all
 * @returns {Promise<string>}
 */
export async function functionErrorMessage(error, fallback = "Request failed") {
  if (!error) return fallback;
  const detail = reasonFromBody(await functionErrorBody(error));
  return detail || error.message || fallback;
}

/** HTTP status of a function error (401 = expired session), or null. */
export function functionErrorStatus(error) {
  const s = error?.context?.status ?? error?.status;
  return Number.isFinite(s) ? s : null;
}

/**
 * One reading of an invoke() result for a function that answers refusals with a non-2xx status (a 429 arrives as
 * { data: null, error } and the body is only on error.context). Reads the error body once.
 *   body    the function's JSON answer: `data` on a 2xx, otherwise the error's reply body (or null)
 *   status  the HTTP status of a failed call, or null
 *   reason  the first readable reason in the body (reasonFromBody), or ""
 *   message reason, else the error's own message, else "" (for showing to a person)
 * @param {{ data?: any, error?: Error|null }} result what supabase.functions.invoke returned
 * @returns {Promise<{ body: object|null, status: number|null, reason: string, message: string }>}
 */
export async function functionReply(result) {
  const { data, error } = result || {};
  const body = error ? await functionErrorBody(error) : (data && typeof data === "object" ? data : null);
  const reason = reasonFromBody(body);
  return { body, status: functionErrorStatus(error), reason, message: reason || error?.message || "" };
}
